import { PlusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Form, Row, Col, message, Select, Switch } from "antd";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import GoBackButton from "../../../components/common/buttons/GoBackButton";
import { useNavigate, useParams } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { useEffect } from "react";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import {
  getDeviceByIdRequest,
  updateDeviceRequest,
} from "../../../api/requests/device";

const updateDeviceSchemaResolver = yupResolver(
  yup.object().shape({
    active: yup.boolean(),
    type: yup
      .string()
      .oneOf(["PROCESSOR", "FARMER", "CARRIER", "ASSISTANT", "OWNER", "ADMIN"]),
    name: yup.string(),
    businessDetails: yup.string(),
    address: yup.object().shape({
      street: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zipCode: yup.string(),
      country: yup.string().length(2, "Country code must be 2 characters"),
      location: yup.object().shape({
        coordinates: yup.array().of(yup.number()),
      }),
    }),
    customAttributes: yup.object().shape({
      size: yup
        .number()
        .positive("Size must be positive")
        .integer("Size must be an integer"),
    }),
  }),
);

function UpdateDevicePage() {
  const params = useParams();
  const { id } = params;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: deviceDetails } = useQuery({
    queryKey: ["device", id],
    queryFn: async () => {
      const res = await getDeviceByIdRequest({
        id,
        params: {},
      });
      return res.data?.data;
    },
  });

  const { mutateAsync: updateDevice, isPending: updatingDevice } = useMutation({
    mutationFn: async (data) => {
      const res = await updateDeviceRequest({ id, data });
      return res.data;
    },
    mutationKey: ["device", id],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["devices"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(-1);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: updateDeviceSchemaResolver,
  });

  // console.log("errors---->", errors);

  async function onSubmit(data) {
    // console.log(data);
    const res = await updateDevice(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (deviceDetails) {
      const {
        _id,
        type,
        active,
        name,
        address,
        phone,
        email,
        businessDetails,
        customAttributes = {},
        updatedBy,
      } = deviceDetails;

      reset({
        type,
        active,
        name,
        address,
        phone,
        email,
        businessDetails,
        customAttributes,
        updatedBy,
      });
    }
  }, [deviceDetails, reset]);

  return (
    <div className="flex flex-col gap-6">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 px-6 pb-6">
        <div className="flex items-center gap-5">
          <GoBackButton />
        </div>
        <div className="flex flex-col gap-6 rounded-xl border-2">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <div className="flex items-center gap-2 text-2xl">
                <PlusCircleOutlined />
                <div>Device Details</div>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
                loading={updatingDevice}
              >
                Update
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Name"
                    name="name"
                    validateStatus={errors.name ? "error" : ""}
                    help={errors.name && errors.name.message}
                  >
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <Input {...field} placeholder="Name" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Business Details"
                    name="businessDetails"
                    validateStatus={errors.businessDetails ? "error" : ""}
                    help={
                      errors.businessDetails && errors.businessDetails.message
                    }
                  >
                    <Controller
                      control={control}
                      name="businessDetails"
                      render={({ field }) => (
                        <Input {...field} placeholder="Business Details" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Type"
                    name="type"
                    validateStatus={errors.type ? "error" : ""}
                    help={errors.type && errors.type.message}
                  >
                    <Controller
                      control={control}
                      name="type"
                      render={({ field }) => (
                        <Select {...field} placeholder="Select Type">
                          <Select.Option value="PROCESSOR">
                            Processor
                          </Select.Option>
                          <Select.Option value="FARMER">Farmer</Select.Option>
                          <Select.Option value="CARRIER">Carrier</Select.Option>
                          <Select.Option value="ASSISTANT">
                            Assistant
                          </Select.Option>
                          <Select.Option value="OWNER">Owner</Select.Option>
                          <Select.Option value="ADMIN">Admin</Select.Option>
                        </Select>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Active"
                    name="active"
                    validateStatus={errors.active ? "error" : ""}
                    help={errors.active && errors.active.message}
                  >
                    <Controller
                      control={control}
                      name="active"
                      render={({ field }) => <Switch {...field} />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Street"
                    name="address.street"
                    validateStatus={errors.address?.street ? "error" : ""}
                    help={errors.address?.street?.message}
                  >
                    <Controller
                      control={control}
                      name="address.street"
                      render={({ field }) => (
                        <Input {...field} placeholder="Street" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    name="address.city"
                    validateStatus={errors.address?.city ? "error" : ""}
                    help={errors.address?.city?.message}
                  >
                    <Controller
                      control={control}
                      name="address.city"
                      render={({ field }) => (
                        <Input {...field} placeholder="City" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="State"
                    name="address.state"
                    validateStatus={errors.address?.state ? "error" : ""}
                    help={errors.address?.state?.message}
                  >
                    <Controller
                      control={control}
                      name="address.state"
                      render={({ field }) => (
                        <Input {...field} placeholder="State" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Zip Code"
                    name="address.zipCode"
                    validateStatus={errors.address?.zipCode ? "error" : ""}
                    help={errors.address?.zipCode?.message}
                  >
                    <Controller
                      control={control}
                      name="address.zipCode"
                      render={({ field }) => (
                        <Input {...field} placeholder="Zip Code" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Country"
                    name="address.country"
                    validateStatus={errors.address?.country ? "error" : ""}
                    help={errors.address?.country?.message}
                  >
                    <Controller
                      control={control}
                      name="address.country"
                      render={({ field }) => (
                        <Select {...field} placeholder="Select Country">
                          <Select.Option value="US">
                            United States
                          </Select.Option>
                          <Select.Option value="CA">Canada</Select.Option>
                          {/* Add more country options as needed */}
                        </Select>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Phone Numbers"
                    name="phone"
                    validateStatus={errors.phone ? "error" : ""}
                    help={errors.phone && errors.phone.message}
                  >
                    <Controller
                      control={control}
                      name="phone"
                      render={({ field }) => (
                        <Select
                          mode="tags"
                          {...field}
                          placeholder="Enter phone numbers (press Enter to add)"
                          tokenSeparators={[",", " "]}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email Addresses"
                    name="email"
                    validateStatus={errors.email ? "error" : ""}
                    help={errors.email && errors.email.message}
                  >
                    <Controller
                      control={control}
                      name="email"
                      render={({ field }) => (
                        <Select
                          mode="tags"
                          {...field}
                          placeholder="Enter email addresses (press Enter to add)"
                          tokenSeparators={[",", " "]}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default UpdateDevicePage;
